import React, { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormInput } from "@/components/FormFields/FormInput";
import { FormSelect } from "@/components/FormFields/FormSelect";
import { FormTextarea } from "@/components/FormFields/FormTextarea";
import { FormCheckbox } from "@/components/FormFields/FormCheckbox";
import { Button } from "@/components/Button";
import { useToast } from "@/hooks/use-toast";
import { Form } from "@/components/ui/form";
import { Link } from "react-router-dom";

const contactFormSchema = z.object({
  firstName: z.string().min(1, {
    message: "First name is required"
  }),
  lastName: z.string().min(1, {
    message: "Last name is required"
  }),
  email: z.string().email({
    message: "Please enter a valid email address"
  }),
  interest: z.string().min(1, {
    message: "Please select an option"
  }),
  message: z.string().min(1, {
    message: "Message is required"
  }),
  mailingList: z.boolean().default(false)
});
type ContactFormValues = z.infer<typeof contactFormSchema>;
const interestOptions = [{
  value: "placeholder",
  label: "Select an Option"
}, {
  value: "contract-analysis",
  label: "Contract Analysis"
}, {
  value: "document-drafting",
  label: "Document Drafting"
}, {
  value: "practice-management",
  label: "Practice Management"
}, {
  value: "compliance-automation",
  label: "Compliance Automation"
}, {
  value: "case-management",
  label: "Case Management AI"
}, {
  value: "other",
  label: "Other"
}];
export default function ContactForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const {
    toast
  } = useToast();
  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      interest: "",
      message: "",
      mailingList: false
    }
  });
  const onSubmit = async (data: ContactFormValues) => {
    setIsSubmitting(true);
    try {
      const webhookUrl = import.meta.env.VITE_CONTACT_WEBHOOK_URL;
      
      if (!webhookUrl) {
        throw new Error("Contact webhook URL not configured - please set VITE_CONTACT_WEBHOOK_URL in your environment variables");
      }
      
      const webhookResponse = await fetch(webhookUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(data)
      });
      if (!webhookResponse.ok) {
        throw new Error("Failed to submit the form");
      }
      setIsSuccess(true);
      toast({
        title: "Success",
        description: "Thank you for contacting Docket Labs! We'll get back to you soon."
      });
      form.reset();
      setTimeout(() => {
        setIsSuccess(false);
      }, 5000);
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        title: "Error",
        description: "There was a problem submitting your form. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl p-8">
      <h2 className="text-2xl font-bold mb-6">Contact DocketLabs</h2>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormInput form={form} name="firstName" label="First Name" placeholder="Enter your first name" />
            
            <FormInput form={form} name="lastName" label="Last Name" placeholder="Enter your last name" />
          </div>
          
          <FormInput form={form} name="email" label="Work Email" placeholder="Enter your work email" type="email" />
          
          <FormSelect form={form} name="interest" label="Primary Interest" placeholder="Select an option" options={interestOptions} />
          
          <FormTextarea form={form} name="message" label="Message" placeholder="Tell us how we can help" maxLength={2000} />
          
          <FormCheckbox form={form} name="mailingList">
            I'd like to receive updates on legal AI insights and news from Docket Labs. <Link to="/privacy-policy" className="text-primary hover:underline">Privacy policy</Link>.
          </FormCheckbox>
          
          <Button variant="primary" className="w-full" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Sending..." : "Send Message"}
          </Button>
        </form>
      </Form>
    </div>;
}
